import React from 'react';
import { Routes, Route } from 'react-router-dom';
import MainLayout from './components/MainLayout';
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import { NotificationProvider } from './context/NotificationContext';
import UserProfile from './pages/UserProfile';
import ProfileView from './pages/ProfileView';
import { useTranslation } from './context/LanguageContext';
import ProductList from './pages/ProductList';
import ProductForm from './pages/ProductForm';
import CategoryList from './pages/CategoryList';
import CategoryForm from './pages/CategoryForm';
import SupplierList from './pages/SupplierList';
import SupplierForm from './pages/SupplierForm';
import EmployeeList from './pages/EmployeeList';
import EmployeeForm from './pages/EmployeeForm';
import MediaLibrary from './pages/MediaLibrary';
import SettingsPage from './pages/SettingsPage';
import SystemParamsPage from './pages/SystemParamsPage';
import SystemAttributeMappingPage from './pages/SystemAttributeMappingPage';
import AttributeSetPage from './pages/AttributeSetPage';
import AttributeForm from './pages/AttributeForm';
import AttributeSetFormEnhanced from './pages/AttributeSetFormEnhanced';
import DashboardPage from './pages/DashboardPage';
import BusinessRolePermissionPanel from './pages/BusinessRolePermissionPanel';
import OrganizationManagement from './pages/OrganizationManagement';
import DepartmentForm from './pages/DepartmentForm';
import PositionForm from './pages/PositionForm';
import TokenManagement from './pages/TokenManagement';
import ResponsiblePersonAssignment from './components/ResponsiblePersonAssignment';
import SetupPage from './pages/SetupPage';
import HelpPage from './pages/HelpPage';
import CategoryAttributeRulePage from './pages/CategoryAttributeRulePage';
import CurrencyExchangeRatesPage from './pages/CurrencyExchangeRatesPage';
import ProfitRatesPage from './pages/ProfitRatesPage';
import CronSettingsPage from './pages/CronSettingsPage';
import CustomerList from './pages/customer/CustomerList';
import CustomerView from './pages/customer/CustomerView';
import StoreAuthorizationList from './pages/channel/StoreAuthorizationList';
import StoreAuthorizationForm from './pages/channel/StoreAuthorizationForm';
import AttributeMappingPage from './pages/channel/AttributeMappingPage';
import ChannelCategorySyncPage from './pages/channel/ChannelCategorySyncPage';
import ChannelEavList from './pages/channel/ChannelEavList';
import ChannelEavForm from './pages/channel/ChannelEavForm';
import TaskList from './pages/TaskList';
import TaskForm from './pages/TaskForm';
import ProtectedRoute from './components/ProtectedRoute';
import SetupGuard from './components/SetupGuard';

const AppRoutes = () => {
  const { t } = useTranslation();
  return (
    <NotificationProvider>
      <Routes>
      {/* 公开路由：登录、修改密码 */}
      <Route path="/login" element={<Login />} />
      <Route path="/change-password" element={<ChangePassword />} />
      
      {/* 所有受保护的路由（需要token） */}
      <Route element={<ProtectedRoute />}>
        {/* Setup页面需要token才能访问 */}
        <Route path="/setup" element={<SetupPage />} />
        
        {/* SetupGuard检查系统是否已初始化，已初始化才显示内容 */}
        <Route element={<SetupGuard />}>
          <Route path="/" element={<MainLayout><DashboardPage /></MainLayout>} />
          <Route path="/products" element={<MainLayout><ProductList /></MainLayout>} />
          <Route path="/products/:id/assign" element={
            <MainLayout>
              <ResponsiblePersonAssignment entityType="product" entityLabel={t('entity.product')} />
            </MainLayout>
          } />
          <Route path="/products/edit/:id" element={<MainLayout><ProductForm key="product-edit" /></MainLayout>} />
          <Route path="/products/view/:id" element={<MainLayout><ProductForm key="product-view" viewOnly={true} /></MainLayout>} />
          <Route path="/products/new" element={<MainLayout><ProductForm /></MainLayout>} />
          <Route path="/categories" element={<MainLayout><CategoryList /></MainLayout>} />
          <Route path="/categories/new" element={<MainLayout><CategoryForm /></MainLayout>} />
          <Route path="/categories/edit/:id" element={<MainLayout><CategoryForm /></MainLayout>} />
          <Route path="/categories/view/:id" element={<MainLayout><CategoryForm viewOnly={true} /></MainLayout>} />
          <Route path="/suppliers" element={<MainLayout><SupplierList /></MainLayout>} />
          <Route path="/suppliers/:id/assign" element={
            <MainLayout>
              <ResponsiblePersonAssignment entityType="supplier" entityLabel={t('entity.supplier')} />
            </MainLayout>
          } />
          <Route path="/suppliers/new" element={<MainLayout><SupplierForm /></MainLayout>} />
          <Route path="/suppliers/edit/:id" element={<MainLayout><SupplierForm /></MainLayout>} />
          <Route path="/suppliers/view/:id" element={<MainLayout><SupplierForm viewOnly={true} /></MainLayout>} />
          <Route path="/employees" element={<MainLayout><EmployeeList /></MainLayout>} />
          <Route path="/employees/new" element={<MainLayout><EmployeeForm /></MainLayout>} />
          <Route path="/employees/edit/:id" element={<MainLayout><EmployeeForm /></MainLayout>} />
          <Route path="/employees/view/:id" element={<MainLayout><EmployeeForm viewOnly={true} /></MainLayout>} />
          <Route path="/profile" element={<MainLayout><UserProfile /></MainLayout>} />
          <Route path="/profile/view" element={<MainLayout><ProfileView /></MainLayout>} />
          <Route path="/profile/change-password" element={<MainLayout><ChangePassword /></MainLayout>} />
          <Route path="/media-library" element={<MainLayout><MediaLibrary /></MainLayout>} />
          <Route path="/tasks" element={<MainLayout><TaskList /></MainLayout>} />
          <Route path="/tasks/new" element={<MainLayout><TaskForm /></MainLayout>} />
          <Route path="/tasks/view/:id" element={<MainLayout><TaskForm viewOnly={true} /></MainLayout>} />
          <Route path="/tasks/edit/:id" element={<MainLayout><TaskForm /></MainLayout>} />
          <Route path="/permissions" element={<MainLayout><BusinessRolePermissionPanel /></MainLayout>} />
          <Route path="/settings/attributes" element={<MainLayout><SettingsPage /></MainLayout>} />
          <Route path="/settings/params" element={<MainLayout><SystemParamsPage /></MainLayout>} />
          <Route path="/settings/attribute-sets" element={<MainLayout><AttributeSetPage /></MainLayout>} />
          <Route path="/attributes/new" element={<MainLayout><AttributeForm /></MainLayout>} />
          <Route path="/attributes/edit/:id" element={<MainLayout><AttributeForm /></MainLayout>} />
          <Route path="/attributes/view/:id" element={<MainLayout><AttributeForm viewOnly={true} /></MainLayout>} />
          <Route path="/attribute-sets/new" element={<MainLayout><AttributeSetFormEnhanced /></MainLayout>} />
          <Route path="/attribute-sets/edit/:id" element={<MainLayout><AttributeSetFormEnhanced /></MainLayout>} />
          <Route path="/attribute-sets/view/:id" element={<MainLayout><AttributeSetFormEnhanced viewOnly={true} /></MainLayout>} />
          <Route path="/organization" element={<MainLayout><OrganizationManagement /></MainLayout>} />
          <Route path="/departments/new" element={<MainLayout><DepartmentForm /></MainLayout>} />
          <Route path="/departments/edit/:id" element={<MainLayout><DepartmentForm /></MainLayout>} />
          <Route path="/departments/view/:id" element={<MainLayout><DepartmentForm viewOnly={true} /></MainLayout>} />
          <Route path="/positions/new" element={<MainLayout><PositionForm /></MainLayout>} />
          <Route path="/positions/edit/:id" element={<MainLayout><PositionForm /></MainLayout>} />
          <Route path="/positions/view/:id" element={<MainLayout><PositionForm viewOnly={true} /></MainLayout>} />
          <Route path="/tokens" element={<MainLayout><TokenManagement readOnly={false} /></MainLayout>} />
          <Route path="/help" element={<MainLayout><HelpPage /></MainLayout>} />
          <Route path="/settings/category-attribute-rules" element={<MainLayout><CategoryAttributeRulePage /></MainLayout>} />
          <Route path="/channel/categories" element={<MainLayout><ChannelCategorySyncPage /></MainLayout>} />
          <Route path="/channel/system-attribute-mapping" element={<MainLayout><SystemAttributeMappingPage /></MainLayout>} />
          <Route path="/settings/currency-exchange-rates" element={<MainLayout><CurrencyExchangeRatesPage /></MainLayout>} />
          <Route path="/settings/profit-rates" element={<MainLayout><ProfitRatesPage /></MainLayout>} />
          <Route path="/settings/cron" element={<MainLayout><CronSettingsPage /></MainLayout>} />
          <Route path="/channel/stores" element={<MainLayout><StoreAuthorizationList /></MainLayout>} />
          <Route path="/channel/stores/new" element={<MainLayout><StoreAuthorizationForm /></MainLayout>} />
          <Route path="/channel/stores/edit/:id" element={<MainLayout><StoreAuthorizationForm /></MainLayout>} />
          <Route path="/channel/eav-products" element={<MainLayout><ChannelEavList /></MainLayout>} />
          <Route path="/channel/eav-products/new" element={<MainLayout><ChannelEavForm /></MainLayout>} />
          <Route path="/channel/eav-products/edit/:id" element={<MainLayout><ChannelEavForm /></MainLayout>} />
          <Route path="/channel/eav-products/view/:id" element={<MainLayout><ChannelEavForm viewOnly={true} /></MainLayout>} />
          <Route path="/channel/eav-products/:id/assign" element={<MainLayout><ResponsiblePersonAssignment entityType="channel_product" entityLabel={t('entity.channel_product')} baseApiPath="/api/channel/eav/products" /></MainLayout>} />
          <Route path="/customers" element={<MainLayout><CustomerList /></MainLayout>} />
          <Route path="/customers/view/:id" element={<MainLayout><CustomerView /></MainLayout>} />
          <Route path="/channel/attribute-mapping" element={<MainLayout><AttributeMappingPage /></MainLayout>} />
        </Route>
      </Route>
    </Routes>
    </NotificationProvider>
  );
};

export default AppRoutes;